import {
  CONFIG_ELEMENTS,
  TestAnswerFeedbackDisplayOption,
  TestAnswerFeedbackSelectOption,
} from '@vedalib/editor/lib/elements'
import { rtValueToText, textToRtValue } from '@vedalib/rich-text'
import { useUpdateEffect } from 'ahooks'

import { buildTestAnnouncement, useLiveFeedback } from 'components/Editor/EditorElements/courseKit/CourseKitUtils'
import TestFeedback from 'components/Editor/EditorElements/courseKit/TestFeedback'
import { useElementRootStyles } from 'components/Editor/EditorElements/hooks/useElementRootStyles'
import { useShuffleRadioOrCheckboxItems } from 'components/Editor/EditorElements/hooks/useShuffleRadioOrCheckboxItems'
import { getFeedbackValue } from 'components/Editor/EditorElements/utils/getFeedbackValue'
import ScreenReaderOnlyText from 'components/Editor/styled/ScreenReaderOnlyText'
import { RichTextViewer } from 'components/form/RichText/RichTextViewer'
import { setQuestion } from 'services/Store/Project/actions'
import { PreviewMode } from 'services/Store/Project/enums'
import { useProjectContext, useProjectDispatch } from 'services/Store/Project/hooks'
import { getBlockState } from 'services/Store/Project/selectors'
import { useGetEntityAnalyticsConfig } from 'services/lms/hooks/useGetEntityAnalyticsConfig'
import { extendTestData } from 'utils/test/qaData'

import type { TestSingleChoiceElementType } from '../testSingleChoiceTypes'
import Radio from './Radio'
import * as s from './TestSingleChoiceElement.module.scss'

const TestSingleChoiceElementPreview: TestSingleChoiceElementType = ({
  element,
  mode,
  styles,
  state,
  block,
  font,
  feedback,
}) => {
  const isPdf = mode.previewMode === PreviewMode.pdf
  const { id: elementId, value } = element
  const dispatch = useProjectDispatch()
  const blockResult = useProjectContext(getBlockState, block?.uuid)?.result
  const { entityAnalyticsConfig, sendXapiData, baseObjectExtensions } = useGetEntityAnalyticsConfig(
    {
      blockId: block?.uuid || '',
      elementType: CONFIG_ELEMENTS.testSingleChoice.type,
      elementId,
    },
  )

  const valueItems = value.items || []
  const isViewMode = mode.previewMode !== PreviewMode.editor
  const disabled = Boolean(!isViewMode || blockResult)
  const isRandom = value.isRandom && isViewMode && !isPdf

  const validIds = valueItems.filter((item) => item.isValid).map((item) => item.value)
  const randomIndexes = useShuffleRadioOrCheckboxItems(valueItems.length, isRandom)
  const feedbackValue = getFeedbackValue(value)

  const isGeneralFb =
    value.displayFeedback && value.displayOption !== TestAnswerFeedbackDisplayOption.perOption
  const isOptionFb =
    value.displayFeedback && value.displayOption !== TestAnswerFeedbackDisplayOption.general

  const onInternalChange = (value: string) => {
    const data = entityAnalyticsConfig.interacted({
      resultExtensions: {
        selectionItem: value,
        unSelectionItem: state?.value?.[0] || undefined,
        labelSelectionItems: rtValueToText(valueItems.find((item) => item.value === value)?.label),
        labelUnSelectionItems: rtValueToText(
          valueItems.find((item) => item.value === state?.value?.[0])?.label,
        ),
      },
      objectExtensions: baseObjectExtensions,
      elementId: element.id,
    })
    sendXapiData(data)
    dispatch(
      setQuestion({
        elementId,
        value: { value: [value] },
        isReady: true,
        blockId: block?.uuid || '',
      }),
    )
  }

  const rootStyles = useElementRootStyles(styles.root)
  const announce = useLiveFeedback('polite')

  useUpdateEffect(() => {
    if (!blockResult) {
      return
    }
    const displayedItems = isRandom ? randomIndexes.map((i) => valueItems[i]) : valueItems
    const selectedId = state?.value?.[0]

    const correctItem = displayedItems.find((item) => validIds.includes(item.value))
    const selectedItem = selectedId ? displayedItems.find((item) => item.value === selectedId) : null

    announce(
      buildTestAnnouncement(
        blockResult.isValid,
        correctItem ? rtValueToText(correctItem.label) : undefined,
        selectedItem ? rtValueToText(selectedItem.label) : undefined,
      ),
    )
  }, [blockResult])

  return (
    <>
      <ScreenReaderOnlyText as='h3' id={elementId}>
        Single choice
      </ScreenReaderOnlyText>
      <div aria-labelledby={elementId} role='radiogroup' style={rootStyles}>
        {valueItems.map((item, index) => {
          const randomIndex = randomIndexes[index]
          const itemToShow = isRandom ? valueItems[randomIndex] : item
          const currentIds = state?.value || []
          const isIncludeValidId = validIds && validIds.includes(itemToShow?.value)
          const isIncludeCurrentId = validIds && currentIds?.includes(itemToShow?.value)
          const isVisibleAnswer = isIncludeCurrentId || isIncludeValidId

          const selectedValue = state?.value?.[0] || null
          const displayedItems = isRandom ? randomIndexes.map((i) => valueItems[i]) : valueItems
          const selectedIndex = displayedItems.findIndex((item) => item.value === selectedValue)
          const showFb = isPdf
            ? value.displayFeedback &&
              value.displayOption !== TestAnswerFeedbackDisplayOption.general
            : value.feedback === TestAnswerFeedbackSelectOption.selected && isOptionFb
              ? blockResult && selectedIndex === index
              : isOptionFb

          return (
            <div
              className={s.wrapItem}
              key={index}
              style={{ marginBottom: styles.offset.marginTop }}
            >
              <TestFeedback
                blockUuid={block?.uuid}
                feedbackValue={feedbackValue}
                font={font}
                isTrueAnswer={isIncludeValidId}
                label={rtValueToText(itemToShow?.label)}
                mode={mode}
                rtProps={{
                  value: itemToShow?.feedback,
                }}
                showAnswerFb={!!isVisibleAnswer}
                showFeedback={showFb}
                styles={feedback}
              >
                <Radio
                  active={selectedIndex < 0 ? index === 0 : selectedIndex === index}
                  checked={currentIds.includes(itemToShow?.value)}
                  disabled={disabled}
                  name={elementId}
                  onChange={onInternalChange}
                  styles={styles}
                  testData={extendTestData({
                    index,
                    label: itemToShow?.label ? rtValueToText(itemToShow?.label) : '',
                  })}
                  value={itemToShow?.value}
                >
                  <RichTextViewer styles={font} value={itemToShow?.label || textToRtValue('')} />
                </Radio>
              </TestFeedback>
            </div>
          )
        })}
        <TestFeedback
          blockUuid={block?.uuid}
          feedbackValue={feedbackValue}
          font={font}
          isTrueAnswer={blockResult?.isValid}
          mode={mode}
          rtProps={{
            value: value.generalFeedback,
          }}
          showFeedback={isPdf ? isGeneralFb : isGeneralFb && !!blockResult}
          styles={feedback}
        />
      </div>
    </>
  )
}

export default TestSingleChoiceElementPreview
